import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Axios from "axios";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Spin,
  Tag,
  message,
} from "antd";
import moment from "moment";
import Header from "../../reusables/Header";
import MerchantTabs from "../../reusables/MerchantTabs";
import "./index.scss";

// Public hotel booking engine page (loystar-shop).
// Routes:
//   /book/:slug           - shareable direct booking link (slug = hotel booking_engine_slug)
//   /:merchantpage/hotel  - resolved via merchant.hotel_booking_engine_slug (future)
//
// Backed by:
//   GET  /api/v2/hotel/booking_engine/:slug
//   GET  /api/v2/hotel/booking_engine/:slug/availability?check_in&check_out&adults&children
//   POST /api/v2/hotel/booking_engine/:slug/reservations
const API_BASE = `${process.env.REACT_APP_BASE_URL}/api/v2`;

const Hotel = () => {
  const { slug } = useParams();

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  const [checkIn, setCheckIn] = useState(moment());
  const [checkOut, setCheckOut] = useState(moment().add(1, "days"));
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);

  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // { check_in, check_out, nights, room_types: [] }

  const [bookingRoom, setBookingRoom] = useState(null); // selected room_type
  const [selectedRatePlan, setSelectedRatePlan] = useState(null);
  const [guest, setGuest] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [specialRequests, setSpecialRequests] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Axios.get(`${API_BASE}/hotel/booking_engine/${slug}`)
      .then((r) => {
        if (cancelled) return;
        setInfo(r.data);
        // If the booking engine is tied to a merchant urewards slug, persist
        // it so MerchantTabs can render cross-links to the shop/services tabs.
        if (r.data?.merchant_urewards_id) {
          try {
            localStorage.setItem(
              "merchant_details",
              JSON.stringify({
                id: r.data.merchant_id,
                email: r.data.merchant_email,
                business_name: r.data.merchant_business_name,
                uRewardsId: r.data.merchant_urewards_id,
              })
            );
          } catch (e) {
            // ignore localStorage errors
          }
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e.message);
      })
      .finally(() => !cancelled && setLoadingInfo(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const search = async () => {
    if (!checkIn || !checkOut || checkOut.isSameOrBefore(checkIn)) {
      message.error("Check-out must be after check-in");
      return;
    }
    setSearching(true);
    setError(null);
    setSearchResults(null);
    try {
      const r = await Axios.get(
        `${API_BASE}/hotel/booking_engine/${slug}/availability`,
        {
          params: {
            check_in: checkIn.format("YYYY-MM-DD"),
            check_out: checkOut.format("YYYY-MM-DD"),
            adults,
            children,
          },
        }
      );
      setSearchResults(r.data);
      if (!r.data?.room_types?.length) {
        message.info("No rooms available for the selected dates");
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSearching(false);
    }
  };

  const openBookingModal = (room) => {
    setBookingRoom(room);
    setSelectedRatePlan(room.rate_plans?.[0] || null);
  };

  const closeBookingModal = () => {
    setBookingRoom(null);
    setSelectedRatePlan(null);
    setGuest({ first_name: "", last_name: "", email: "", phone: "" });
    setSpecialRequests("");
  };

  const submitBooking = async () => {
    if (!bookingRoom) return;
    if (!guest.first_name || !guest.last_name || !guest.email || !guest.phone) {
      message.error("Please fill all guest fields");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        adults,
        children,
        special_requests: specialRequests || undefined,
        guest,
        rooms: [
          {
            room_type_id: bookingRoom.room_type_id,
            rate_plan_id: selectedRatePlan?.id,
            check_in: searchResults.check_in,
            check_out: searchResults.check_out,
            adults,
            children,
          },
        ],
      };
      const r = await Axios.post(
        `${API_BASE}/hotel/booking_engine/${slug}/reservations`,
        payload
      );
      setConfirmation(r.data.confirmation_number || `#${r.data?.reservation?.id}`);
      closeBookingModal();
      message.success("Reservation created");
    } catch (e) {
      const errs = e?.response?.data?.errors;
      setError(
        (Array.isArray(errs) && errs.join(", ")) ||
          e?.response?.data?.error ||
          e.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const computedTotal = (() => {
    if (!selectedRatePlan || !searchResults) return null;
    return Number(selectedRatePlan.base_rate) * Number(searchResults.nights);
  })();

  if (loadingInfo) {
    return (
      <div className="hotel-page hotel-page--loading">
        <Spin size="large" />
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="hotel-page hotel-page--error">
        <h2>Booking engine unavailable</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="hotel-page">
      <Header
        merchantee={info?.merchant_business_name || info?.property_name}
        titleComponent={null}
      />

      {info?.merchant_urewards_id && (
        <MerchantTabs
          merchantSlug={info.merchant_urewards_id}
          activeKey="hotel"
        />
      )}

      <div className="hotel-page__content">
        <section className="hotel-page__hero">
          <h1>{info.property_name}</h1>
          {info.property_description && (
            <p className="hotel-page__desc">{info.property_description}</p>
          )}
          {info.amenities?.length > 0 && (
            <div className="hotel-page__amenities">
              {info.amenities.map((a) => (
                <Tag key={a}>{a}</Tag>
              ))}
            </div>
          )}
        </section>

        {confirmation && (
          <Card className="hotel-page__confirmation">
            <h3>Reservation confirmed!</h3>
            <p>
              Your confirmation number is <strong>{confirmation}</strong>.
              Please keep this for your records.
            </p>
            <Button type="link" onClick={() => setConfirmation(null)}>
              Make another reservation
            </Button>
          </Card>
        )}

        <Card className="hotel-page__search" title="Find a room">
          <div className="hotel-page__search-row">
            <Form.Item label="Check-in">
              <DatePicker
                value={checkIn}
                onChange={setCheckIn}
                disabledDate={(d) => d && d.isBefore(moment().startOf("day"))}
                allowClear={false}
              />
            </Form.Item>
            <Form.Item label="Check-out">
              <DatePicker
                value={checkOut}
                onChange={setCheckOut}
                disabledDate={(d) =>
                  d && d.isSameOrBefore(checkIn || moment(), "day")
                }
                allowClear={false}
              />
            </Form.Item>
            <Form.Item label="Adults">
              <InputNumber
                min={1}
                max={20}
                value={adults}
                onChange={(v) => setAdults(v || 1)}
              />
            </Form.Item>
            <Form.Item label="Children">
              <InputNumber
                min={0}
                max={20}
                value={children}
                onChange={(v) => setChildren(v || 0)}
              />
            </Form.Item>
            <Form.Item label=" ">
              <Button type="primary" loading={searching} onClick={search}>
                Search
              </Button>
            </Form.Item>
          </div>
        </Card>

        {error && searchResults === null && (
          <div className="hotel-page__inline-error">{error}</div>
        )}

        {searchResults?.room_types?.length > 0 && (
          <div className="hotel-page__results">
            <h2>
              Available rooms — {searchResults.nights} night
              {searchResults.nights === 1 ? "" : "s"}
            </h2>
            {searchResults.room_types.map((rt) => (
              <Card key={rt.room_type_id} className="hotel-page__room-card">
                <div className="hotel-page__room-info">
                  <h3>{rt.name}</h3>
                  {rt.description && <p>{rt.description}</p>}
                  <div className="hotel-page__room-meta">
                    <span>Sleeps {rt.max_occupancy}</span>
                    <span>{rt.available} available</span>
                  </div>
                  {rt.amenities?.length > 0 && (
                    <div className="hotel-page__amenities">
                      {rt.amenities.map((a) => (
                        <Tag key={a}>{a}</Tag>
                      ))}
                    </div>
                  )}
                </div>
                <div className="hotel-page__room-action">
                  <div className="hotel-page__rate">
                    <span className="hotel-page__rate-amount">
                      {info.currency_code} {Number(rt.base_rate).toLocaleString()}
                    </span>
                    <span className="hotel-page__rate-suffix">/ night</span>
                  </div>
                  <Button type="primary" onClick={() => openBookingModal(rt)}>
                    Book now
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {(info.house_rules || info.cancellation_policy) && (
          <section className="hotel-page__policies">
            {info.house_rules && (
              <div>
                <h3>House rules</h3>
                <p>{info.house_rules}</p>
              </div>
            )}
            {info.cancellation_policy && (
              <div>
                <h3>Cancellation policy</h3>
                <p>{info.cancellation_policy}</p>
              </div>
            )}
          </section>
        )}
      </div>

      <Modal
        open={!!bookingRoom}
        onCancel={closeBookingModal}
        title={bookingRoom ? `Reserve: ${bookingRoom.name}` : ""}
        onOk={submitBooking}
        okText={submitting ? "Reserving…" : "Confirm reservation"}
        okButtonProps={{ loading: submitting }}
        destroyOnClose
      >
        {bookingRoom && (
          <Form layout="vertical">
            {bookingRoom.rate_plans?.length > 1 && (
              <Form.Item label="Rate plan">
                <div className="hotel-page__rate-plans">
                  {bookingRoom.rate_plans.map((rp) => (
                    <Button
                      key={rp.id}
                      type={
                        selectedRatePlan?.id === rp.id ? "primary" : "default"
                      }
                      onClick={() => setSelectedRatePlan(rp)}
                    >
                      {rp.name} — {info.currency_code}{" "}
                      {Number(rp.base_rate).toLocaleString()}/night
                    </Button>
                  ))}
                </div>
              </Form.Item>
            )}

            <Form.Item label="First name" required>
              <Input
                value={guest.first_name}
                onChange={(e) =>
                  setGuest({ ...guest, first_name: e.target.value })
                }
              />
            </Form.Item>
            <Form.Item label="Last name" required>
              <Input
                value={guest.last_name}
                onChange={(e) =>
                  setGuest({ ...guest, last_name: e.target.value })
                }
              />
            </Form.Item>
            <Form.Item label="Email" required>
              <Input
                type="email"
                value={guest.email}
                onChange={(e) => setGuest({ ...guest, email: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Phone" required>
              <Input
                value={guest.phone}
                onChange={(e) => setGuest({ ...guest, phone: e.target.value })}
              />
            </Form.Item>
            <Form.Item label="Special requests">
              <Input.TextArea
                rows={3}
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
              />
            </Form.Item>

            {computedTotal !== null && (
              <div className="hotel-page__total">
                <span>Total for stay</span>
                <strong>
                  {info.currency_code} {computedTotal.toLocaleString()}
                </strong>
              </div>
            )}
            <p className="hotel-page__total-note">
              Payment will be collected at the property. Online deposit
              support coming soon.
            </p>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default Hotel;
